import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Upload, Loader2, Package, Image as ImageIcon, AlertTriangle, CheckCircle2, X, Search, ChevronDown, ChevronRight, Palette } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import ProtectedImg from "@/components/b2b/ProtectedImg";

const SIZE_OPTIONS = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];
const COLOR_OPTIONS = ["Biały", "Czarny", "Szary", "Czerwony", "Niebieski", "Granatowy", "Zielony", "Żółty", "Pomarańczowy", "Różowy", "Fioletowy", "Brązowy", "Beżowy"];
const PLACES = ["Przód", "Tył", "Lewy rękaw", "Prawy rękaw", "Inne"];
const TECHNIQUES = ["DTF", "Haft", "Silikon", "Flock", "Inna"];
const pln = (g) => `${((g || 0) / 100).toFixed(2).replace(".", ",")} zł`;
const vKey = (c, s) => `${c}|${s}`;
const newMarking = () => ({ place: "Przód", position_desc: "", technique: "DTF", width_cm: "", height_cm: "", dimension_type: "target", graphic_desc: "", color: "", production_file_id: "", viz_file_id: "", notes: "", size_overrides: [] });

const EMPTY = {
  name: "", subtitle: "", description: "", price_grosz: 0, photos: [], colors: [], color_photos: {}, sizes: [],
  disabled_variants: [], size_table: "", sort_order: 0, no_branding: false,
  base_sku: "", base_color: "", base_notes: "", variant_index: {}, variants: [], markings: [], production_notes: "",
};

const Collapsible = ({ title, subtitle, actions, defaultOpen = false, children, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-border bg-background/60" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button type="button" data-testid={testid ? `${testid}-toggle` : undefined} onClick={() => setOpen((o) => !o)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
          <span className="truncate text-xs font-bold text-amber-300">{title}</span>
          {subtitle && <span className="truncate text-[11px] text-muted-foreground">{subtitle}</span>}
        </button>
        {actions}
      </div>
      {open && <div className="space-y-2 px-3 pb-3">{children}</div>}
    </div>
  );
};

export const B2bProducts = ({ storeId, storeName }) => {
  const confirm = useConfirm();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/b2b/admin/stores/${storeId}/products`).then((r) => setProducts(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [storeId]);

  const openNew = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setOpen(true); };

  const del = async (p) => {
    if (!(await confirm({ title: `Usunąć produkt „${p.name}"?`, description: "Operacja nieodwracalna.", confirmText: "Usuń", danger: true }))) return;
    try { await api.delete(`/b2b/admin/products/${p.id}`); toast.success("Produkt usunięty"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-lg border border-border bg-background/50 p-4 space-y-3" data-testid={`b2b-products-${storeId}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-wider flex items-center gap-2"><Package className="h-4 w-4 text-amber-400" />Produkty sklepu ({products.length})</p>
        <Button data-testid={`b2b-add-product-${storeId}`} size="sm" variant="outline" className="h-8 gap-1.5" onClick={openNew}><Plus className="h-3.5 w-3.5" />Dodaj produkt</Button>
      </div>
      {products.length === 0 && <p className="text-[11px] text-muted-foreground px-1">Brak produktów. Dodaj pierwszy produkt prywatny.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {products.map((p) => (
          <div key={p.id} data-testid={`b2b-product-${p.id}`} className="rounded-lg border border-border bg-card overflow-hidden">
            <button type="button" onClick={() => openEdit(p)} className="block w-full text-left">
              <div className="aspect-[4/3] bg-[#0D1117] grid place-items-center overflow-hidden">
                {p.photos?.[0] ? <img src={`${API}/files/${p.photos[0]}`} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-8 w-8 text-muted-foreground" />}
              </div>
              <div className="p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold truncate">{p.name || "(bez nazwy)"}</p>
                  <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${p.status === "published" ? "bg-emerald-400/15 text-emerald-300" : "bg-secondary text-muted-foreground"}`}>{p.status === "published" ? "Opublikowany" : "Roboczy"}</span>
                </div>
                <p className="text-xs font-mono text-amber-300">{pln(p.price_grosz)}</p>
                <p className="text-[10px] text-muted-foreground">{(p.colors || []).length} kol. · {(p.sizes || []).length} rozm. · {(p.variants || []).length} war.</p>
                {(p.completeness_gaps || []).length > 0
                  ? <p className="text-[10px] text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{p.completeness_gaps.length} braków</p>
                  : <p className="text-[10px] text-emerald-400 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />kompletny</p>}
              </div>
            </button>
            <div className="border-t border-border px-3 py-1.5 text-right">
              <Button data-testid={`b2b-product-del-${p.id}`} size="sm" variant="ghost" className="h-7 text-rose-400 gap-1" onClick={() => del(p)}><Trash2 className="h-3.5 w-3.5" />Usuń</Button>
            </div>
          </div>
        ))}
      </div>
      {open && <ProductForm storeId={storeId} storeName={storeName} product={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
};

const MarkingEditor = ({ m, tid, label, onPatch, onDelete, onUploadFile }) => {
  const addOverride = () => onPatch({ size_overrides: [...(m.size_overrides || []), { size: "", width_cm: "", height_cm: "", position_desc: "" }] });
  const updOverride = (j, patch) => onPatch({ size_overrides: (m.size_overrides || []).map((o, k) => (k === j ? { ...o, ...patch } : o)) });
  const delOverride = (j) => onPatch({ size_overrides: (m.size_overrides || []).filter((_, k) => k !== j) });
  return (
    <Collapsible defaultOpen testid={`b2b-pf-marking-${tid}`} title={label} subtitle={[m.place, m.technique].filter(Boolean).join(" · ")}
      actions={<Button size="icon" variant="ghost" data-testid={`b2b-pf-marking-del-${tid}`} className="h-7 w-7 text-rose-400" onClick={onDelete}><Trash2 className="h-4 w-4" /></Button>}>
      <div className="grid sm:grid-cols-2 gap-2">
        <div><Label className="text-[11px]">Miejsce</Label>
          <Select value={m.place || ""} onValueChange={(v) => onPatch({ place: v })}><SelectTrigger data-testid={`b2b-pf-marking-place-${tid}`} className="mt-1 h-9"><SelectValue placeholder="Miejsce" /></SelectTrigger>
            <SelectContent>{PLACES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-[11px]">Technika</Label>
          <Select value={m.technique || ""} onValueChange={(v) => onPatch({ technique: v })}><SelectTrigger data-testid={`b2b-pf-marking-tech-${tid}`} className="mt-1 h-9"><SelectValue placeholder="Technika" /></SelectTrigger>
            <SelectContent>{TECHNIQUES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div><Label className="text-[11px]">Dokładny opis położenia</Label><Textarea data-testid={`b2b-pf-marking-pos-${tid}`} value={m.position_desc} onChange={(e) => onPatch({ position_desc: e.target.value })} rows={1} className="mt-1" placeholder="np. lewa pierś, 8 cm od szwu ramienia" /></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        <div><Label className="text-[11px]">Szerokość (cm)</Label><Input data-testid={`b2b-pf-marking-w-${tid}`} type="number" min="0" step="0.1" value={m.width_cm ?? ""} onChange={(e) => onPatch({ width_cm: e.target.value === "" ? "" : Number(e.target.value) })} className="mt-1 h-9" /></div>
        <div><Label className="text-[11px]">Wysokość (cm)</Label><Input data-testid={`b2b-pf-marking-h-${tid}`} type="number" min="0" step="0.1" value={m.height_cm ?? ""} onChange={(e) => onPatch({ height_cm: e.target.value === "" ? "" : Number(e.target.value) })} className="mt-1 h-9" /></div>
        <div className="col-span-2"><Label className="text-[11px]">Wymiar</Label>
          <Select value={m.dimension_type || "target"} onValueChange={(v) => onPatch({ dimension_type: v })}><SelectTrigger data-testid={`b2b-pf-marking-dim-${tid}`} className="mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="target">Docelowy</SelectItem><SelectItem value="max">Maksymalny</SelectItem></SelectContent></Select></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div><Label className="text-[11px]">Opis grafiki</Label><Input data-testid={`b2b-pf-marking-graphic-${tid}`} value={m.graphic_desc} onChange={(e) => onPatch({ graphic_desc: e.target.value })} className="mt-1 h-9" /></div>
        <div><Label className="text-[11px]">Kolor znakowania</Label><Input data-testid={`b2b-pf-marking-color-${tid}`} value={m.color} onChange={(e) => onPatch({ color: e.target.value })} className="mt-1 h-9" /></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <label className="flex items-center gap-1.5 rounded-md border border-dashed border-border px-2.5 py-1.5 text-[11px] cursor-pointer hover:border-amber-400">
          <Upload className="h-3.5 w-3.5" />{m.production_file_id ? "Plik ✓ (zmień)" : "Plik produkcyjny"}
          <input data-testid={`b2b-pf-marking-file-${tid}`} type="file" className="hidden" onChange={(e) => onUploadFile("production_file_id", e)} />
        </label>
        <label className="flex items-center gap-1.5 rounded-md border border-dashed border-border px-2.5 py-1.5 text-[11px] cursor-pointer hover:border-amber-400">
          <ImageIcon className="h-3.5 w-3.5" />{m.viz_file_id ? "Wizualizacja ✓ (zmień)" : "Wizualizacja rozmieszczenia"}
          <input data-testid={`b2b-pf-marking-viz-${tid}`} type="file" accept="image/*" className="hidden" onChange={(e) => onUploadFile("viz_file_id", e)} />
        </label>
        {m.viz_file_id && <ProtectedImg fileId={m.viz_file_id} className="h-9 w-9 rounded object-cover border border-border" />}
      </div>
      <div><Label className="text-[11px]">Uwagi dla produkcji</Label><Textarea data-testid={`b2b-pf-marking-notes-${tid}`} value={m.notes} onChange={(e) => onPatch({ notes: e.target.value })} rows={1} className="mt-1" /></div>
      <div className="rounded-md border border-border/60 p-2">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold text-muted-foreground">Wyjątki dla rozmiaru ({(m.size_overrides || []).length})</p>
          <Button size="sm" variant="ghost" data-testid={`b2b-pf-marking-add-override-${tid}`} className="h-6 gap-1 text-[11px]" onClick={addOverride}><Plus className="h-3 w-3" />Wyjątek</Button>
        </div>
        {(m.size_overrides || []).map((o, j) => (
          <div key={j} className="mt-1 grid grid-cols-2 sm:grid-cols-5 gap-1.5 items-center">
            <Input placeholder="Rozmiar" data-testid={`b2b-pf-ovr-size-${tid}-${j}`} value={o.size} onChange={(e) => updOverride(j, { size: e.target.value })} className="h-8 text-[11px]" />
            <Input placeholder="szer. cm" type="number" step="0.1" value={o.width_cm ?? ""} onChange={(e) => updOverride(j, { width_cm: e.target.value === "" ? "" : Number(e.target.value) })} className="h-8 text-[11px]" />
            <Input placeholder="wys. cm" type="number" step="0.1" value={o.height_cm ?? ""} onChange={(e) => updOverride(j, { height_cm: e.target.value === "" ? "" : Number(e.target.value) })} className="h-8 text-[11px]" />
            <Input placeholder="położenie" value={o.position_desc} onChange={(e) => updOverride(j, { position_desc: e.target.value })} className="h-8 text-[11px] sm:col-span-1" />
            <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={() => delOverride(j)}><X className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
    </Collapsible>
  );
};

const ProductForm = ({ storeId, storeName, product, onClose, onSaved }) => {
  const initForm = () => {
    const base = { ...EMPTY, ...product };
    base.color_photos = base.color_photos || {};
    base.variants = base.variants || [];
    // Migracja legacy: znakowania na poziomie produktu → pierwszy wariant.
    if ((base.markings || []).length && !base.variants.length) {
      base.variants = [{ id: "", name: "Wariant 1", print_color: "", photo: "", markings: base.markings }];
    }
    base.markings = [];
    return base;
  };
  const [f, setF] = useState(initForm);
  const isEdit = !!(f.id || product?.id);
  const [priceZl, setPriceZl] = useState(product?.price_grosz ? (product.price_grosz / 100).toString() : "");
  const [bases, setBases] = useState([]);
  const [baseDetail, setBaseDetail] = useState(null);
  const [baseOpen, setBaseOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingColor, setUploadingColor] = useState("");

  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  useEffect(() => { api.get("/b2b/admin/base-products").then((r) => setBases(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!f.base_sku) { setBaseDetail(null); return; }
    api.get(`/b2b/admin/base-products/${encodeURIComponent(f.base_sku)}`).then((r) => setBaseDetail(r.data)).catch(() => setBaseDetail(null));
  }, [f.base_sku]);

  const colorOptions = useMemo(() => [...COLOR_OPTIONS, ...((f.colors || []).filter((c) => !COLOR_OPTIONS.includes(c)))], [f.colors]);
  const sizeOptions = useMemo(() => [...SIZE_OPTIONS, ...((f.sizes || []).filter((s) => !SIZE_OPTIONS.includes(s)))], [f.sizes]);
  const colors = useMemo(() => colorOptions.filter((c) => (f.colors || []).includes(c)), [colorOptions, f.colors]);
  const sizes = useMemo(() => sizeOptions.filter((s) => (f.sizes || []).includes(s)), [sizeOptions, f.sizes]);
  const toggleColor = (c) => set({ colors: (f.colors || []).includes(c) ? f.colors.filter((x) => x !== c) : [...(f.colors || []), c] });
  const toggleSize = (s) => set({ sizes: (f.sizes || []).includes(s) ? f.sizes.filter((x) => x !== s) : [...(f.sizes || []), s] });
  const disabled = new Set(f.disabled_variants || []);

  const uploadOne = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.id;
  };

  const buildPayload = () => {
    const normCm = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const normMark = (m) => ({ ...m, width_cm: normCm(m.width_cm), height_cm: normCm(m.height_cm), size_overrides: (m.size_overrides || []).map((o) => ({ ...o, width_cm: normCm(o.width_cm), height_cm: normCm(o.height_cm) })) });
    const variants = (f.variants || []).map((v) => ({ ...v, markings: (v.markings || []).map(normMark) }));
    const cp = {}; colors.forEach((c) => { if ((f.color_photos || {})[c]) cp[c] = f.color_photos[c]; });
    return {
      store_id: storeId,
      name: f.name, subtitle: f.subtitle, description: f.description,
      price_grosz: Math.round((parseFloat(priceZl) || 0) * 100),
      photos: f.photos || [], colors, color_photos: cp, sizes, disabled_variants: [...disabled],
      size_table: f.size_table, sort_order: Number(f.sort_order) || 0, no_branding: !!f.no_branding,
      base_sku: f.base_sku, base_color: f.base_color, base_notes: f.base_notes,
      variant_index: f.variant_index || {}, variants, markings: [], production_notes: f.production_notes,
    };
  };

  const save = async (publishAfter) => {
    setBusy(true);
    try {
      const payload = buildPayload();
      const pid = f.id || product?.id;
      const { data } = (f.id || product?.id)
        ? await api.put(`/b2b/admin/products/${pid}`, payload)
        : await api.post("/b2b/admin/products", payload);
      if (publishAfter) {
        try { await api.post(`/b2b/admin/products/${data.id}/publish`); toast.success("Produkt opublikowany"); }
        catch (e) {
          const d = e?.response?.data?.detail;
          if (d?.gaps) { toast.error(`Nie opublikowano — braki: ${d.gaps.length}`); setF((prev) => ({ ...prev, _gaps: d.gaps, id: data.id })); setBusy(false); return; }
          throw e;
        }
      } else { toast.success(isEdit ? "Zapisano zmiany" : "Produkt zapisany (roboczy)"); }
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setUploadingPhoto(true);
    try {
      const ids = [];
      for (const file of files) ids.push(await uploadOne(file));
      set({ photos: [...(f.photos || []), ...ids] });
    } catch (err) { toast.error(errMsg(err)); } finally { setUploadingPhoto(false); }
  };

  const uploadColorPhoto = async (c, e) => {
    const file = (e.target.files || [])[0]; e.target.value = "";
    if (!file) return;
    setUploadingColor(c);
    try { const id = await uploadOne(file); set({ color_photos: { ...(f.color_photos || {}), [c]: id } }); }
    catch (err) { toast.error(errMsg(err)); } finally { setUploadingColor(""); }
  };
  const clearColorPhoto = (c) => { const cp = { ...(f.color_photos || {}) }; delete cp[c]; set({ color_photos: cp }); };

  const setVIndex = (c, s, val) => set({ variant_index: { ...(f.variant_index || {}), [vKey(c, s)]: val } });
  const toggleVariantCell = (c, s) => {
    const k = vKey(c, s); const n = new Set(disabled); n.has(k) ? n.delete(k) : n.add(k);
    set({ disabled_variants: [...n] });
  };

  // ---- warianty (kolory nadruku / logowania) ----
  const addVariant = () => set({ variants: [...(f.variants || []), { id: "", name: `Wariant ${(f.variants || []).length + 1}`, print_color: "", photo: "", markings: [] }] });
  const updVariant = (vi, patch) => set({ variants: (f.variants || []).map((v, i) => (i === vi ? { ...v, ...patch } : v)) });
  const delVariant = (vi) => set({ variants: (f.variants || []).filter((_, i) => i !== vi) });
  const uploadVariantPhoto = async (vi, e) => {
    const file = (e.target.files || [])[0]; e.target.value = "";
    if (!file) return;
    try { const id = await uploadOne(file); updVariant(vi, { photo: id }); toast.success("Zdjęcie wariantu dodane"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const addMarking = (vi) => updVariant(vi, { markings: [...((f.variants[vi].markings) || []), newMarking()] });
  const updMarking = (vi, mi, patch) => updVariant(vi, { markings: (f.variants[vi].markings || []).map((m, j) => (j === mi ? { ...m, ...patch } : m)) });
  const delMarking = (vi, mi) => updVariant(vi, { markings: (f.variants[vi].markings || []).filter((_, j) => j !== mi) });
  const uploadMarkingFile = async (vi, mi, field, e) => {
    const file = (e.target.files || [])[0]; e.target.value = "";
    if (!file) return;
    try { const id = await uploadOne(file); updMarking(vi, mi, { [field]: id }); toast.success("Plik dodany"); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const gaps = f._gaps || product?.completeness_gaps || [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="b2b-product-form" className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">{isEdit ? "Edycja produktu" : "Nowy produkt"} — {storeName}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">Dane „Widok klienta" są widoczne dla klienta. „Karta produkcyjna" jest wewnętrzna. Żadne pole nie jest wymagane do zapisu roboczego.</DialogDescription>
        </DialogHeader>

        {gaps.length > 0 && (
          <div data-testid="b2b-gaps" className="rounded-lg border border-amber-500/40 bg-amber-400/10 p-3 text-xs text-amber-200">
            <p className="font-bold flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />Braki do publikacji ({gaps.length}):</p>
            <ul className="mt-1 list-disc pl-5 space-y-0.5">{gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
          </div>
        )}

        {/* ===== WIDOK KLIENTA ===== */}
        <section className="space-y-3 rounded-lg border border-border p-4">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber-400">Widok klienta</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Nazwa produktu</Label><Input data-testid="b2b-pf-name" value={f.name} onChange={(e) => set({ name: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Podtytuł</Label><Input data-testid="b2b-pf-subtitle" value={f.subtitle} onChange={(e) => set({ subtitle: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label className="text-xs">Opis</Label><Textarea data-testid="b2b-pf-desc" value={f.description} onChange={(e) => set({ description: e.target.value })} rows={2} className="mt-1" /></div>
          <div><Label className="text-xs">Cena (zł)</Label><Input data-testid="b2b-pf-price" type="number" min="0" step="0.01" value={priceZl} onChange={(e) => setPriceZl(e.target.value)} className="mt-1 sm:max-w-[200px]" /></div>

          <div data-testid="b2b-pf-colors">
            <Label className="text-xs">Kolory produktu (zaznacz z listy)</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {colorOptions.map((c) => {
                const on = (f.colors || []).includes(c);
                return (
                  <button type="button" key={c} data-testid={`b2b-pf-color-${c}`} onClick={() => toggleColor(c)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${on ? "border-amber-400 bg-amber-400/15 text-amber-200" : "border-border text-muted-foreground hover:border-amber-400/50"}`}>{c}</button>
                );
              })}
            </div>
            {colors.length > 0 && (
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="b2b-pf-color-photos">
                {colors.map((c) => {
                  const ph = (f.color_photos || {})[c];
                  return (
                    <div key={c} className="flex items-center gap-2 rounded-lg border border-border/70 p-2">
                      <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md bg-[#0D1117]">
                        {ph ? <img src={`${API}/files/${ph}`} alt="" className="h-full w-full object-contain" /> : <span className="grid h-full place-items-center text-muted-foreground"><Palette className="h-4 w-4" /></span>}
                        {ph && <button type="button" onClick={() => clearColorPhoto(c)} className="absolute right-0 top-0 rounded bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-semibold">{c}</p>
                        <label data-testid={`b2b-pf-color-photo-${c}`} className="mt-0.5 inline-flex cursor-pointer items-center gap-1 text-[11px] text-amber-400 hover:underline">
                          {uploadingColor === c ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}{ph ? "Zmień zdjęcie" : "Dodaj zdjęcie"}
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadColorPhoto(c, e)} />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div data-testid="b2b-pf-sizes">
            <Label className="text-xs">Rozmiary (zaznacz z listy)</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {sizeOptions.map((s) => {
                const on = (f.sizes || []).includes(s);
                return (
                  <button type="button" key={s} data-testid={`b2b-pf-size-${s}`} onClick={() => toggleSize(s)}
                    className={`min-w-10 rounded-md border px-2.5 py-1 text-xs font-bold transition ${on ? "border-amber-400 bg-amber-400/15 text-amber-200" : "border-border text-muted-foreground hover:border-amber-400/50"}`}>{s}</button>
                );
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Kolejność</Label><Input data-testid="b2b-pf-sort" type="number" value={f.sort_order} onChange={(e) => set({ sort_order: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Tabela rozmiarów (tekst)</Label><Textarea data-testid="b2b-pf-sizetable" value={f.size_table} onChange={(e) => set({ size_table: e.target.value })} rows={1} className="mt-1" /></div>
          </div>
          <div>
            <Label className="text-xs">Zdjęcia / wizualizacje (ogólne)</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {(f.photos || []).map((ph) => (
                <div key={ph} className="relative h-20 w-20 rounded-lg overflow-hidden border border-border">
                  <img src={`${API}/files/${ph}`} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => set({ photos: f.photos.filter((x) => x !== ph) })} className="absolute right-0.5 top-0.5 rounded bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label data-testid="b2b-pf-photo-upload" className="grid h-20 w-20 cursor-pointer place-items-center rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-amber-400">
                {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadPhotos} />
              </label>
            </div>
          </div>
          {colors.length > 0 && sizes.length > 0 && (
            <div>
              <Label className="text-xs">Dostępne warianty (odznacz, aby wyłączyć)</Label>
              <div className="mt-1 overflow-x-auto">
                <table className="text-[11px]"><tbody>
                  {colors.map((c) => (
                    <tr key={c}>
                      <td className="pr-2 font-semibold whitespace-nowrap">{c}</td>
                      {sizes.map((s) => (
                        <td key={s} className="px-1 py-0.5 text-center">
                          <label className="flex flex-col items-center gap-0.5">
                            <span className="text-muted-foreground">{s}</span>
                            <Checkbox data-testid={`b2b-pf-variant-${c}-${s}`} checked={!disabled.has(vKey(c, s))} onCheckedChange={() => toggleVariantCell(c, s)} />
                          </label>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody></table>
              </div>
            </div>
          )}
        </section>

        {/* ===== KARTA PRODUKCYJNA ===== */}
        <section className="space-y-3 rounded-lg border border-indigo-500/40 bg-indigo-500/5 p-4">
          <p className="font-mono text-[11px] uppercase tracking-widest text-indigo-300">Karta produkcyjna (wewnętrzna)</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Kartoteka odzieży bazowej (EMPRO)</Label>
              <Popover open={baseOpen} onOpenChange={setBaseOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" role="combobox" data-testid="b2b-pf-base-sku" className="mt-1 w-full justify-between gap-2 font-normal">
                    <span className="truncate">{f.base_sku ? `${f.base_sku}${baseDetail?.name ? " — " + baseDetail.name : ""}` : "Wyszukaj kartotekę bazową…"}</span>
                    <Search className="h-4 w-4 shrink-0 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[360px]" align="start">
                  <Command>
                    <CommandInput placeholder="Szukaj (nazwa, SKU, typ)…" data-testid="b2b-pf-base-search" />
                    <CommandList>
                      <CommandEmpty>Brak wyników</CommandEmpty>
                      <CommandGroup>
                        {bases.map((b) => (
                          <CommandItem key={b.sku} value={`${b.name || ""} ${b.sku || ""} ${b.type || ""} ${b.brand || ""}`} data-testid={`b2b-pf-base-opt-${b.sku}`}
                            onSelect={() => { set({ base_sku: b.sku }); setBaseOpen(false); }}>
                            <div className="min-w-0"><p className="truncate text-sm">{b.name}</p><p className="truncate text-xs text-muted-foreground">{b.sku}{b.type ? " · " + b.type : ""}</p></div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {baseDetail && <p className="mt-1 text-[10px] text-muted-foreground">Baza: {(baseDetail.colors || []).length} kol. · {(baseDetail.sizes || []).length} rozm.{Object.keys(baseDetail.wapro_index || {}).length ? " · ma indeksy WAPRO" : ""}</p>}
            </div>
            <div><Label className="text-xs">Kolor odzieży bazowej</Label><Input data-testid="b2b-pf-base-color" value={f.base_color} onChange={(e) => set({ base_color: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label className="text-xs">Uwagi do odzieży bazowej</Label><Textarea data-testid="b2b-pf-base-notes" value={f.base_notes} onChange={(e) => set({ base_notes: e.target.value })} rows={1} className="mt-1" /></div>

          {colors.length > 0 && sizes.length > 0 && (
            <div>
              <Label className="text-xs">Indeksy wariantów (kolor × rozmiar)</Label>
              <p className="text-[10px] text-muted-foreground">Wpisz indeks wewn./WAPRO dla aktywnych wariantów. Wyłączone warianty pomijane.</p>
              <div className="mt-1 overflow-x-auto">
                <table className="text-[11px] border-collapse">
                  <thead><tr><th className="pr-2"></th>{sizes.map((s) => <th key={s} className="px-1 font-semibold">{s}</th>)}</tr></thead>
                  <tbody>
                    {colors.map((c) => (
                      <tr key={c}>
                        <td className="pr-2 font-semibold whitespace-nowrap">{c}</td>
                        {sizes.map((s) => (
                          <td key={s} className="px-0.5 py-0.5">
                            {disabled.has(vKey(c, s))
                              ? <div className="h-8 w-20 grid place-items-center text-muted-foreground/40">—</div>
                              : <Input data-testid={`b2b-pf-idx-${c}-${s}`} value={(f.variant_index || {})[vKey(c, s)] || ""} onChange={(e) => setVIndex(c, s, e.target.value)} className="h-8 w-20 text-[11px] font-mono" />}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* warianty + znakowania */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none">
              <Switch data-testid="b2b-pf-no-branding" checked={!!f.no_branding} onCheckedChange={(v) => set({ no_branding: v })} />
              Produkt bez znakowania
            </label>
            {!f.no_branding && <Button data-testid="b2b-pf-add-variant" size="sm" variant="outline" className="h-8 gap-1.5" onClick={addVariant}><Plus className="h-3.5 w-3.5" />Dodaj wariant</Button>}
          </div>

          {!f.no_branding && (f.variants || []).length === 0 && (
            <p className="text-[11px] text-muted-foreground">Brak wariantów. Dodaj wariant (np. „Czarne logowanie"), a następnie dodaj do niego znakowania.</p>
          )}

          {!f.no_branding && (f.variants || []).map((v, vi) => (
            <Collapsible key={vi} defaultOpen testid={`b2b-pf-variant-card-${vi}`}
              title={`Wariant ${vi + 1}${v.name && v.name !== `Wariant ${vi + 1}` ? `: ${v.name}` : ""}`} subtitle={v.print_color}
              actions={<Button size="icon" variant="ghost" data-testid={`b2b-pf-variant-del-${vi}`} className="h-7 w-7 text-rose-400" onClick={() => delVariant(vi)}><Trash2 className="h-4 w-4" /></Button>}>
              <div className="grid sm:grid-cols-2 gap-2">
                <div><Label className="text-[11px]">Nazwa wariantu</Label><Input data-testid={`b2b-pf-variant-name-${vi}`} value={v.name} onChange={(e) => updVariant(vi, { name: e.target.value })} className="mt-1 h-9" placeholder="np. Czarne logowanie" /></div>
                <div><Label className="text-[11px]">Kolor nadruku / logowania</Label><Input data-testid={`b2b-pf-variant-print-${vi}`} value={v.print_color} onChange={(e) => updVariant(vi, { print_color: e.target.value })} className="mt-1 h-9" placeholder="np. Czarny" /></div>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-border bg-[#0D1117]">
                  {v.photo ? <img src={`${API}/files/${v.photo}`} alt="" className="h-full w-full object-contain" /> : <span className="grid h-full place-items-center text-muted-foreground"><ImageIcon className="h-5 w-5" /></span>}
                  {v.photo && <button type="button" onClick={() => updVariant(vi, { photo: "" })} className="absolute right-0.5 top-0.5 rounded bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>}
                </div>
                <label data-testid={`b2b-pf-variant-photo-${vi}`} className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-dashed border-border px-2.5 py-1.5 text-[11px] hover:border-amber-400">
                  <Upload className="h-3.5 w-3.5" />{v.photo ? "Zmień zdjęcie wariantu" : "Dodaj zdjęcie wariantu"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadVariantPhoto(vi, e)} />
                </label>
              </div>

              <div className="flex items-center justify-between pt-1">
                <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Znakowania ({(v.markings || []).length})</p>
                <Button size="sm" variant="outline" data-testid={`b2b-pf-add-marking-${vi}`} className="h-7 gap-1 text-[11px]" onClick={() => addMarking(vi)}><Plus className="h-3 w-3" />Dodaj znakowanie</Button>
              </div>
              {(v.markings || []).map((m, mi) => (
                <MarkingEditor key={mi} m={m} tid={`${vi}-${mi}`} label={`Znakowanie ${mi + 1}`}
                  onPatch={(patch) => updMarking(vi, mi, patch)} onDelete={() => delMarking(vi, mi)}
                  onUploadFile={(field, e) => uploadMarkingFile(vi, mi, field, e)} />
              ))}
            </Collapsible>
          ))}

          <div><Label className="text-xs">Uwagi wewnętrzne (produkcja)</Label><Textarea data-testid="b2b-pf-prod-notes" value={f.production_notes} onChange={(e) => set({ production_notes: e.target.value })} rows={2} className="mt-1" /></div>
        </section>

        <div className="flex flex-wrap gap-2 justify-end sticky bottom-0 bg-background pt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Anuluj</Button>
          <Button data-testid="b2b-pf-save-draft" variant="outline" onClick={() => save(false)} disabled={busy}>{busy ? "Zapisywanie…" : "Zapisz roboczy"}</Button>
          <Button data-testid="b2b-pf-save-publish" onClick={() => save(true)} disabled={busy} className="bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">Zapisz i opublikuj</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
